import type { TextFieldProps } from '@mui/material/TextField';
import { FilledInputProps, InputProps, OutlinedInputProps, TextField, Theme } from '@mui/material';
import { SxProps } from '@mui/system';
import React, { FC, FocusEventHandler, ReactNode, useMemo } from 'react';
import { NumberFormatValues, NumericFormat } from 'react-number-format';

export type NumberInputProps = {
  label?: ReactNode;
  placeholder?: string;
  value: number | null | undefined;
  onChange(values: NumberFormatValues): void;
  error?: boolean;
  helperText?: ReactNode;
  required?: boolean;
  disabled?: boolean;
  size?: TextFieldProps['size'];
  variant?: 'outlined' | 'filled' | 'standard';
  margin?: 'dense' | 'normal' | 'none';
  fullWidth?: boolean;
  sx?: SxProps<Theme>;

  format?: {
    style?: 'currency' | 'percent' | 'phoneNumber';
    currency?: 'USD' | 'EUR';
    maximumFractionDigits?: number;
    minimumFractionDigits?: number;
  };
  min?: number | undefined | null;
  max?: number | undefined | null;
  withoutOnBlurVoid?: boolean;
  withThousandSeparator?: boolean;
  allowNegative?: boolean;
  allowZero?: boolean;
  inputProps?:
  | Partial<OutlinedInputProps>
  | Partial<FilledInputProps>
  | Partial<InputProps>
  | undefined;
};

const prefixes = {
  USD: '$',
  EUR: '€',
};

export const NumberInput: FC<NumberInputProps> = props => {
  const {
    label,
    placeholder,
    value,
    onChange,
    error,
    helperText,
    required,
    disabled,
    size,
    variant = 'outlined',
    margin = 'normal',
    fullWidth = true,
    sx,
    format,
    min,
    max,
    withoutOnBlurVoid = true,
    withThousandSeparator = true,
    allowNegative = true,
    allowZero = true,
    inputProps,
  } = props;

  const thousandSeparator =
    !withThousandSeparator || format?.style === 'phoneNumber' ? '' : ',';

  const onBlur: FocusEventHandler<HTMLInputElement> | undefined = useMemo(() => {
    if (typeof min === 'number') {
      return e => {
        const value = e.target.value
          .replaceAll(',', '')
          .replace('$', '')
          .replace('€', '')
          .replace('%', '');

        if (withoutOnBlurVoid && (value === '' || value === null || value === undefined)) {
          return;
        }

        if (!value || isNaN(Number(value)) || Number(value) < min) {
          return onChange({ floatValue: min, formattedValue: String(min), value: String(min) });
        }
      };
    }
  }, [min, value, onChange, withoutOnBlurVoid]);

  const prefix = useMemo(() => {
    if (format?.style === 'currency' && format?.currency) {
      return prefixes[format.currency];
    }
  }, [format]);

  const suffix = useMemo(() => {
    if (format?.style === 'percent') {
      return '%';
    }
  }, [format]);

  return (
    <NumericFormat
      InputProps={inputProps}
      customInput={TextField}
      label={label}
      placeholder={placeholder}
      value={typeof value === 'number' ? value : ''}
      onValueChange={onChange}
      onBlur={onBlur}
      error={error}
      helperText={helperText}
      required={required}
      disabled={disabled}
      size={size}
      variant={variant}
      margin={margin}
      fullWidth={fullWidth}
      thousandSeparator={thousandSeparator}
      decimalSeparator="."
      decimalScale={format?.maximumFractionDigits}
      allowNegative={allowNegative}
      prefix={prefix}
      suffix={suffix}
      isAllowed={values => {
        const { floatValue } = values;

        if (typeof floatValue === 'number' && typeof max === 'number' && floatValue > max) {
          return false;
        }

        if (!allowZero && floatValue === 0) {
          return false;
        }

        return true;
      }}
      sx={sx}
    />
  );
};
